import ApexCharts, {ApexOptions} from 'apexcharts'
import React, {useEffect, useId, useRef} from 'react'
import {getCSS, getCSSVariableValue} from '../../../../_metronic/assets/ts/_utils'
import {useThemeMode} from '../../../../_metronic/partials/layout/theme-mode/ThemeModeProvider'
import LoadingChart from '@/app/modules/apps/dashboard/components/LoadingChart'

type Props = {
  className?: string
  color?: string
  series?: any
  categories?: any
  height?: string
  isCurrency?: boolean
  isLoading?: boolean
}

function formatAxisValue(val: unknown, isCurrency: boolean): string {
  const n = typeof val === 'number' ? val : parseFloat(String(val))
  if (!Number.isFinite(n)) return isCurrency ? 'Rp. 0' : '0'
  if (isCurrency) {
    const rounded = Math.abs(n) >= 100 ? Math.round(n) : Math.round(n * 100) / 100
    return (
      'Rp. ' +
      new Intl.NumberFormat('id-ID', {
        maximumFractionDigits: Math.abs(rounded) >= 100 ? 0 : 2,
        minimumFractionDigits: 0,
      }).format(rounded)
    )
  }
  const rounded = Number.isInteger(n) ? n : Math.round(n * 100) / 100
  return String(rounded)
}

const LineChart: React.FC<Props> = ({
  className = '',
  color = 'primary',
  height = '300px',
  series = [],
  categories = [],
  isCurrency = true,
  isLoading = false,
}) => {
  const chartRef = useRef<HTMLDivElement | null>(null)
  const chartDomId = useId().replace(/:/g, '')
  const {mode} = useThemeMode()

  useEffect(() => {
    if (!chartRef.current) return

    const h = parseInt(getCSS(chartRef.current, 'height'))
    const chart = new ApexCharts(
      chartRef.current,
      getLineChartOptions(h, color, series, categories, isCurrency)
    )
    chart.render()

    return () => {
      chart.destroy()
    }
  }, [chartRef, mode, series, categories, isCurrency, color])

  return (
    <div className={className}>
      {isLoading && <LoadingChart />}
      {!isLoading && (
        <div ref={chartRef} id={`line-chart-${chartDomId}`} style={{height}} />
      )}
    </div>
  )
}

export {LineChart}

function getLineChartOptions(
  height: number,
  color: string,
  series: any,
  categories: any,
  isCurrency: boolean
): ApexOptions {
  const labelColor = getCSSVariableValue('--bs-gray-500')
  const borderColor = getCSSVariableValue('--bs-gray-200')
  const baseColor = getCSSVariableValue(`--bs-${color}`)
  const lightColor = getCSSVariableValue(`--bs-${color}-light`) || baseColor + '33'

  return {
    series,
    chart: {
      fontFamily: 'inherit',
      type: 'area',
      height,
      toolbar: {show: false},
      zoom: {enabled: false},
      sparkline: {enabled: false},
    },
    legend: {show: false},
    dataLabels: {enabled: false},
    fill: {
      type: 'gradient',
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.4,
        opacityTo: 0.05,
        stops: [0, 90, 100],
      },
    },
    stroke: {
      curve: 'smooth',
      show: true,
      width: 3,
      colors: [baseColor],
    },
    xaxis: {
      categories,
      axisBorder: {show: false},
      axisTicks: {show: false},
      labels: {
        style: {colors: labelColor, fontSize: '12px'},
      },
      crosshairs: {
        position: 'front',
        stroke: {color: baseColor, width: 1, dashArray: 3},
      },
      tooltip: {enabled: false},
    },
    yaxis: {
      labels: {
        style: {colors: labelColor, fontSize: '12px'},
        formatter: (val) => formatAxisValue(val, isCurrency),
      },
    },
    tooltip: {
      style: {fontSize: '12px'},
      y: {
        formatter: (val) => formatAxisValue(val, isCurrency),
      },
    },
    colors: [baseColor],
    grid: {
      borderColor,
      strokeDashArray: 4,
      yaxis: {lines: {show: true}},
    },
    markers: {
      size: 0,
      colors: [baseColor],
      strokeColors: baseColor,
      strokeWidth: 3,
      hover: {size: 5},
    },
  }
}
